import {useCallback, useEffect, useRef, useState} from 'react';
import usePromoteBranch from './useManageMoves';
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '@/Utils/CommonUtils';
import {useToast} from '@/toast/useToast';
import {
  BranchMove,
  getPromoteUndoTargetId,
} from '@/Screens/Study/utils/moveTreeBranchOps';

/**
 * Fallback window for the undo banner when nothing else finalizes it first.
 * The primary way it ends is a qualifying tree event (see `flush`).
 */
const GRACE_MS = 60000;

type PendingDeleteRecord = {
  type: 'delete';
  chapterId: string | number;
  moveId: string | number;
  label?: string;
  expiresAt: number;
};

type PendingPromoteRecord = {
  type: 'promote';
  chapterId: string | number;
  undoTargetMoveId: string | number;
  label?: string;
  expiresAt: number;
};

type UndoRecord = PendingDeleteRecord | PendingPromoteRecord;

const storageKey = (chapterId: string | number) =>
  `study:move-undo:${chapterId}`;

export type UndoBannerState = {
  type: 'delete' | 'promote';
  message: string;
  expiresAt: number;
};

/**
 * Single-slot, per-chapter undo for move promote/delete. Persisted so it
 * survives a reload: on (re)mount it either resumes the countdown (still
 * within the window) or finalizes immediately (window already elapsed while
 * the app was closed). Ended by whichever comes first — another qualifying
 * tree event via `flush()`, the fallback timer, or an explicit `undo()`.
 *
 * Delete is deferred: nothing is sent to the server until the record is
 * finalized. Promote is applied immediately (non-destructive); the record
 * only remembers how to reverse it.
 */
export default function useMoveTreeUndo(
  chapterId: string | number | undefined | null,
  refetchStudy: () => Promise<void> | void,
  /**
   * Applies a promote to the local tree immediately (see
   * promoteBranchLocally in Study/utils/moveTreeOps.ts), so the move list
   * flips the branch into the mainline the instant the coach clicks —
   * the refetch that follows the API call reconciles with the server.
   * Also used to apply the reverse promote on undo.
   */
  applyLocalPromote?: (moveId: string | number) => void,
) {
  const {mutateAsync: manageBranchAsync, isLoading} = usePromoteBranch();
  const showToast = useToast();

  const [record, setRecord] = useState<UndoRecord | null>(null);
  const recordRef = useRef<UndoRecord | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped whenever a promote or its undo actually re-shapes the tree
  // (moves change which node "owns" a fork), so UI-only per-node state that
  // isn't part of the tree itself - e.g. WorkbookMovesList's manually
  // toggled-open variation chips - can reset to a deterministic baseline
  // instead of carrying over stale toggles from whatever shape the tree
  // happened to be in right before the mutation.
  const [mutationVersion, setMutationVersion] = useState(0);

  // Kept hidden from the tree until the delete actually lands (see
  // commitDelete) - separate from `record`, which clears the instant
  // finalize() runs so the banner disappears right away, well before the
  // network round trip (and the refetch after it) that actually removes the
  // move completes. Without this, the move would flash back into view for
  // that gap - no longer optimistically pruned, but not yet actually gone
  // from movesList either.
  const [finalizingDeleteMoveId, setFinalizingDeleteMoveId] = useState<
    string | number | null
  >(null);

  const setRecordBoth = (next: UndoRecord | null) => {
    recordRef.current = next;
    setRecord(next);
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const persist = useCallback(
    (next: UndoRecord | null) => {
      if (!chapterId) {
        return;
      }
      if (next) {
        setLocalStorage(storageKey(chapterId), next);
      } else {
        removeLocalStorage(storageKey(chapterId));
      }
    },
    [chapterId],
  );

  const commitDelete = useCallback(
    async (moveId: string | number) => {
      setFinalizingDeleteMoveId(moveId);
      try {
        await manageBranchAsync({action: 'delete', move_id: moveId});
      } catch (e) {
        showToast('Failed to delete move', {type: 'Error'});
      } finally {
        await refetchStudy();
        // Only clear it if nothing queued a newer finalizing delete in the
        // meantime (single-slot hook, but commitDelete runs unawaited from
        // finalize, so a rapid second delete could in principle overlap).
        setFinalizingDeleteMoveId(current =>
          current === moveId ? null : current,
        );
      }
    },
    [manageBranchAsync, refetchStudy, showToast],
  );

  const finalize = useCallback(
    (current: UndoRecord | null) => {
      clearTimer();
      setRecordBoth(null);
      persist(null);
      if (current?.type === 'delete') {
        commitDelete(current.moveId);
      }
    },
    [commitDelete, persist],
  );

  const arm = useCallback(
    (next: UndoRecord) => {
      clearTimer();
      const remaining = Math.max(0, next.expiresAt - Date.now());
      timerRef.current = setTimeout(() => finalize(next), remaining);
      setRecordBoth(next);
      persist(next);
    },
    [finalize, persist],
  );

  // Resume or reconcile a persisted record whenever the active chapter changes
  // (covers page reload too, since this runs on mount).
  useEffect(() => {
    clearTimer();
    setRecordBoth(null);
    if (!chapterId) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const stored = (await getLocalStorage(
        storageKey(chapterId),
      )) as UndoRecord | null;
      if (!stored || cancelled) {
        return;
      }
      if (Date.now() >= stored.expiresAt) {
        finalize(stored);
      } else {
        arm(stored);
      }
    })();

    return () => {
      cancelled = true;
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  /** Finalizes whatever is currently pending — call before any other move-tree mutation. */
  const flush = useCallback(() => {
    if (recordRef.current) {
      finalize(recordRef.current);
    }
  }, [finalize]);

  const queueDelete = useCallback(
    (moveId: string | number, label?: string) => {
      if (!chapterId) {
        return;
      }
      if (recordRef.current) {
        finalize(recordRef.current);
      }
      arm({
        type: 'delete',
        chapterId,
        moveId,
        label,
        expiresAt: Date.now() + GRACE_MS,
      });
    },
    [chapterId, finalize, arm],
  );

  const promoteMove = useCallback(
    async (
      moveId: string | number,
      flatById: Map<string, BranchMove>,
      label?: string,
    ) => {
      if (!chapterId) {
        return;
      }
      const undoTargetId = getPromoteUndoTargetId(flatById, moveId);
      if (recordRef.current) {
        finalize(recordRef.current);
      }
      applyLocalPromote?.(moveId);
      setMutationVersion(v => v + 1);
      try {
        await manageBranchAsync({action: 'promote', move_id: moveId});
        if (undoTargetId != null) {
          arm({
            type: 'promote',
            chapterId,
            undoTargetMoveId: undoTargetId,
            label,
            expiresAt: Date.now() + GRACE_MS,
          });
        }
      } catch (e) {
        showToast('Failed to promote move', {type: 'Error'});
      } finally {
        refetchStudy();
      }
    },
    [
      chapterId,
      finalize,
      arm,
      manageBranchAsync,
      refetchStudy,
      showToast,
      applyLocalPromote,
    ],
  );

  const undo = useCallback(async () => {
    const current = recordRef.current;
    if (!current) {
      return;
    }
    clearTimer();
    setRecordBoth(null);
    persist(null);
    if (current.type === 'promote') {
      applyLocalPromote?.(current.undoTargetMoveId);
      setMutationVersion(v => v + 1);
      try {
        await manageBranchAsync({
          action: 'promote',
          move_id: current.undoTargetMoveId,
        });
      } catch (e) {
        showToast('Failed to undo promotion', {type: 'Error'});
      } finally {
        refetchStudy();
      }
    }
    // For 'delete', dropping the record is enough — nothing was ever sent.
  }, [manageBranchAsync, refetchStudy, showToast, persist, applyLocalPromote]);

  const undoBanner: UndoBannerState | null = record
    ? {
        type: record.type,
        message:
          record.type === 'delete'
            ? record.label || 'Move deleted'
            : record.label || 'Promoted to mainline',
        expiresAt: record.expiresAt,
      }
    : null;

  return {
    isLoading,
    pendingDeleteMoveId:
      record?.type === 'delete' ? record.moveId : finalizingDeleteMoveId,
    undoBanner,
    queueDelete,
    promoteMove,
    undo,
    flush,
    mutationVersion,
  };
}
